import { d2lfetch } from 'd2l-fetch/src/index.js';
import { fetchDedupe } from 'd2l-fetch-dedupe';

export const headers = {
  'Content-Type': 'application/json',
};

export const createUrlWithParams = (baseUrl, data) => {
  const params = Object.keys(data).map(key => {
    if (data[key] !== undefined && data[key] !== null) {
      const value = typeof data[key] === 'object' ? JSON.stringify(data[key]) : data[key];
      return [key, value].map(encodeURIComponent).join('=');
    }
    return null;
  }).filter(Boolean).join('&').toString();
  return params.length <= 0 ? baseUrl : `${baseUrl}?${params}`;
};

export class PMClient {
  constructor() {
    d2lfetch.use({ name: 'dedupe', fn: fetchDedupe });
    const base = this.isDev() ? 'http://localhost:3000/' : '';
    this.apiUrl = `${base}api`;
  }

  get session() {
    return this._session;
  }

  set session(session) {
    this._session = session;
  }

  isDev() {
    return window.location.host === 'localhost:8080';
  }

  deployEnvironment(environmentId, branchName) {
    return this.request(`environments/${environmentId}/deploy`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ branchName }),
    }, 'json', `Started a deploy to ${environmentId} from the branch: ${branchName}`);
  }

  fetchEnvironment(id) {
    return this.request(`environments/${id}`);
  }

  fetchEnvironments() {
    return this.request('environments');
  }

  fetchDeployments(environmentId) {
    return this.request(`deployments/${environmentId}`);
  }

  fetchIDPMetadata(environmentId, tenantId, responseFormat = 'metadata-xml') {
    return this.request(`sso/metadata/${environmentId}/${tenantId}?responseFormat=${responseFormat}`, {}, 'text');
  }

  fetchTenantSSO(environmentId, tenantId) {
    return this.request(`tenants/${environmentId}/${tenantId}`);
  }

  fetchTenantSSOById(environmentId, domain) {
    return this.request(`tenants/${environmentId}/domain/${domain}`);
  }

  fetchLoginLink(environmentId, tenantId, userId, domain) {
    return this.request(`tenants/${environmentId}/${tenantId}/login/${userId}/${domain}`, {}, 'text');
  }

  fetchUsers() {
    return this.request('users');
  }

  fetchUser(id) {
    return this.request(`users/${id}`);
  }

  fetchSession() {
    const url = 'session'; // Add a unique query parameter
    return this.request(url, {
      method: 'GET',
      headers: {
        ...headers,
        'Cache-Control': 'no-cache', // Instructs no-cache
      },
    }).then((session) => {
      this.session = session;
      return session;
    });
  }

  isProd() {
    return this.session.appEnvironment === 'prod';
  }

  fetchEnvironmentHealth(environmentId) {
    return this.request(`environments/${environmentId}/health`);
  }

  refreshEnvironmentStatus(environmentId) {
    return this.request(`environments/${environmentId}/status/refresh`, {
      method: 'POST',
      headers,
    });
  }

  fetchToken(environmentId, system) {
    return this.request(`environments/${environmentId}/token/${system}`);
  }

  set apiUrl(apiUrl) {
    this._apiUrl = apiUrl;
  }

  get apiUrl() {
    return this._apiUrl;
  }

  fetchPreferences() {
    return this.request('preferences');
  }

  fetchBranches() {
    return this.request('github/branches');
  }

  isValidDomain(environmentId, domain) {
    return this.request(`/environments/${environmentId}/valid_domain/${domain}`);
  }

  upsertUser(user) {
    return this.request('users', {
      method: 'PUT',
      headers,
      body: JSON.stringify(user),
    }, 'json', `${user.displayName || user.userId} saved successfully`);
  }

  removeUser(user) {
    return this.request(`users/${user.userId}`, {
      method: 'DELETE',
      headers,
    }, 'json', `${user.displayName || user.userId} removed successfully`);
  }

  removeFussUser(environmentId, tenantId, user) {
    return this.request(`users/${environmentId}/${tenantId}/${user.guid}`, {
      method: 'DELETE',
    });
  }

  updatePreferences(preferences) {
    return this.request('preferences', {
      method: 'PUT',
      headers,
      body: JSON.stringify(preferences),
    }, 'json', 'Preferences saved successfully');
  }

  updateTenantSSO(environmentId, tenantSSO) {
    return this.request(`tenants/${environmentId}/${tenantSSO.id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(tenantSSO),
    }, 'json', `${tenantSSO.displayName} saved successfully`);
  }

  standardizeTenantSSO(environmentId, tenantId, tenantSlug) {
    return this.request(`tenants/${environmentId}/${tenantId}/standardize-sso`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ tenantSlug }),
    }, 'json', 'Tenant SSO successfully standardized');
  }

  updateTenantLabels(environmentId, tenantId, tenantLabels) {
    return this.request(`tenants/${environmentId}/${tenantId}/labels`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ tenantLabels }),
    }, 'json', 'Tenant labels updated successfully');
  }

  fetchTasks(environmentId, params) {
    const url = createUrlWithParams(`environments/${environmentId}/tasks`, params);
    return this.request(url);
  }

  removeEnvironment(environmentId) {
    return this.request(`environments/${environmentId}/remove`, {
      method: 'DELETE',
      headers,
    }, 'json', `Successfully removed ${environmentId}`);
  }

  createEnvironment(environment) {
    return this.request('environments', {
      method: 'POST',
      headers,
      body: JSON.stringify(environment),
    }, 'json', `${environment.displayName} created successfully`);
  }

  fetchTenantUsers(environmentId, tenantId) {
    return this.request(`users/${environmentId}/${tenantId}`);
  }

  fetchTenantRoles(environmentId, tenantId) {
    return this.request(`roles/${environmentId}/tenant/${tenantId}`);
  }

  fetchUserRoles(environmentId, userGuid) {
    return this.request(`roles/${environmentId}/user/${userGuid}`);
  }

  assignRole(environmentId, userGuid, roleId) {
    return this.request(`roles/${environmentId}/user/${userGuid}/${roleId}`, {
      method: 'PUT',
      headers,
    });
  }

  removeRole(environmentId, userGuid, roleId) {
    return this.request(`roles/${environmentId}/user/${userGuid}/${roleId}`, {
      method: 'DELETE',
    });
  }

  pushToFuss(environmentId, tenantId, user) {
    return this.request(`users/${environmentId}/${tenantId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(user),
    }, 'json', 'Successfully sent user to FUSS');
  }

  updateEnvironmentConfig(environmentId, config) {
    return this.request(`environments/${environmentId}/config`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(config),
    }, 'json', `${environmentId} config updated successfully`);
  }

  updateDirectusSyncConfig(environmentId, config) {
    return this.request(`environments/${environmentId}/directus-sync`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(config),
    });
  }

  testFussConnection(fussUrl) {
    return this.request(`test/fuss/${fussUrl}`);
  }

  fetchDemoFixtures(environmentId) {
    return this.request(`demo-reset/${environmentId}/fixtures`);
  }

  startDemoReset(environmentId, tenantId, fixture) {
    return this.request(`demo-reset/${environmentId}/${tenantId}/reset`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ fixture }),
    });
  }

  fetchDemoResetStatus(environmentId, executionId) {
    return this.request(`demo-reset/${environmentId}/status/${executionId}`);
  }

  fetchDemoResetHistory(environmentId, tenantId) {
    return this.request(`demo-reset/${environmentId}/${tenantId}/history`);
  }

  createAlert(type, message) {
    const alertEvent = new CustomEvent('kraken-alert-event', {
      detail: { message, type },
      bubbles: true,
      composed: true,
    });
    document.dispatchEvent(alertEvent);
  }

  async request(url, options = {}, responseType = 'json', message) {
    try {
      options.credentials = 'include';
      const response = await d2lfetch.fetch(`${this.apiUrl}/${url}`, options);

      if (response.ok && response.status === 200) {
        message && this.createAlert('success', message);
        return responseType === 'json' ? response.json() : response.text();
      } else if (response.status === 204) {
        return null;
      }
      throw response;
    } catch (e) {
      let message = e?.message;
      if (!message && e.text) message = await e.text();
      this.createAlert('error', message);
      throw e;
    }
  }
}
